import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Newspaper } from "lucide-react";
import SEO from "@/components/common/SEO";
import PageHero from "@/components/common/PageHero";
import { Stagger, StaggerItem } from "@/components/common/Reveal";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return ""; }
}

export default function Insights() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  return (
    <>
      <SEO
        title="Insights | Premier Global Sourcing"
        description="Practical insights on product sourcing, international trading and logistics coordination from Premier Global Sourcing."
        path="/insights"
      />
      <PageHero
        eyebrow="Insights"
        title="Notes on sourcing, trading and logistics"
        intro="Practical perspectives to help you plan cross-border purchases with more confidence."
        breadcrumbs={[{ label: "Insights" }]}
      />

      <section className="container-page py-16 lg:py-24">
        {posts === null ? (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i}>
                <Skeleton className="w-full aspect-video rounded-lg" />
                <Skeleton className="h-5 w-3/4 mt-4" />
                <Skeleton className="h-4 w-full mt-2" />
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[#C3CDD3] bg-white p-14 text-center max-w-xl" data-testid="insights-empty">
            <div className="mx-auto h-12 w-12 rounded-full bg-[#EAF1F4] flex items-center justify-center">
              <Newspaper className="h-6 w-6 text-teal" />
            </div>
            <p className="mt-5 text-[16px] font-medium text-navy">No articles published yet</p>
            <p className="mt-2 text-[14px] text-muted-foreground">Insights will appear here as they are published.</p>
          </div>
        ) : (
          <Stagger className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((p) => (
              <StaggerItem key={p.id}>
                <Link to={`/insights/${p.slug}`} className="group block" data-testid={`insight-card-${p.slug}`}>
                  <div className="relative overflow-hidden rounded-lg border border-[#D9E0E4] bg-[#EAF1F4] aspect-video">
                    {p.cover_image ? (
                      <img src={p.cover_image} alt={p.title} loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Newspaper className="h-8 w-8 text-teal/40" />
                      </div>
                    )}
                  </div>
                  <div className="mt-4 flex items-center gap-3 text-[12px] text-muted-foreground">
                    <span className="font-semibold uppercase tracking-wide text-teal">{p.category}</span>
                    <span>·</span>
                    <span>{formatDate(p.created_at)}</span>
                  </div>
                  <h2 className="mt-2 font-heading font-bold text-[19px] text-navy leading-snug group-hover:text-teal transition-colors">
                    {p.title}
                  </h2>
                  {p.excerpt && <p className="mt-2 text-[14.5px] text-muted-foreground leading-relaxed line-clamp-2">{p.excerpt}</p>}
                  <span className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-teal">
                    Read insight <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        )}
      </section>
    </>
  );
}
